import { useEffect, useState } from "react";
import { MapPin } from "lucide-react";
import api, { inr } from "../../lib/api";

// Stylized India projection: lng 68–97 → x 0–100, lat 6–37 → y 100–0
const proj = (lat, lng) => ({ x: ((lng - 68) / 29) * 100, y: ((37 - lat) / 31) * 100 });

export default function AdminInsights() {
  const [hotspots, setHotspots] = useState(null);

  useEffect(() => {
    api.get("/admin/insights/hotspots").then(({ data }) => setHotspots(data)).catch(() => {});
  }, []);

  const maxNdr = Math.max(1, ...(hotspots || []).map((h) => h.ndr + h.rto));

  return (
    <div data-testid="admin-insights-page">
      <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">SHIPANY Insights</h1>
      <p className="text-sm text-slate-500">Geographic hotspots — NDR/RTO concentration by destination region.</p>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl bg-shipany-navy p-6" data-testid="hotspots-map">
          <h2 className="font-display text-sm font-bold text-white">Hotspot Map</h2>
          <svg viewBox="0 0 100 100" className="mt-4 w-full" style={{ maxHeight: 420 }}>
            <polygon points="30,8 42,4 55,6 68,10 76,14 74,20 82,22 88,26 84,30 74,32 70,38 66,46 60,56 54,66 48,78 44,88 40,80 36,68 32,56 26,44 22,34 18,26 22,18"
              fill="rgba(18,200,245,0.07)" stroke="rgba(18,200,245,0.35)" strokeWidth="0.5" />
            {(hotspots || []).filter((h) => h.lat && h.lng).map((h) => {
              const { x, y } = proj(h.lat, h.lng);
              const intensity = (h.ndr + h.rto) / maxNdr;
              const r = 1.6 + (h.shipments / Math.max(1, ...(hotspots || []).map((x) => x.shipments))) * 3.5;
              const color = intensity > 0.5 ? "#EF4444" : intensity > 0.2 ? "#F59E0B" : "#12C8F5";
              return (
                <g key={h.pin_prefix}>
                  <circle cx={x} cy={y} r={r} fill={color} opacity={0.75}>
                    <animate attributeName="r" values={`${r};${r + 0.8};${r}`} dur="2.4s" repeatCount="indefinite" />
                  </circle>
                  <text x={x + r + 1} y={y + 1} fill="#94A3B8" fontSize="3.2" fontFamily="JetBrains Mono">{h.region}</text>
                </g>
              );
            })}
          </svg>
          <div className="mt-3 flex gap-4 text-[11px] text-slate-400">
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-shipany-cyan" /> Healthy</span>
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-amber-400" /> Watch</span>
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-red-500" /> NDR/RTO hotspot</span>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="hotspots-table">
          <h2 className="font-display text-sm font-bold text-shipany-navy">Region Breakdown</h2>
          <table className="mt-3 w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400"><th className="py-2">Region</th><th>Shipments</th><th>NDR</th><th>RTO</th><th>Delivered</th><th>Revenue</th></tr></thead>
            <tbody>
              {(hotspots || []).map((h) => (
                <tr key={h.pin_prefix} className="border-t border-slate-100" data-testid={`hotspot-${h.pin_prefix}`}>
                  <td className="py-2.5 font-semibold text-shipany-navy"><MapPin className="mr-1 inline h-3.5 w-3.5 text-shipany-blue" />{h.region} <span className="mono text-[10px] text-slate-400">{h.pin_prefix}xxxx</span></td>
                  <td className="mono">{h.shipments}</td>
                  <td className={`mono font-bold ${h.ndr ? "text-orange-600" : "text-slate-400"}`}>{h.ndr}</td>
                  <td className={`mono font-bold ${h.rto ? "text-red-500" : "text-slate-400"}`}>{h.rto}</td>
                  <td className="mono text-emerald-600">{h.delivered}</td>
                  <td className="mono">{inr(h.revenue)}</td>
                </tr>
              ))}
              {hotspots && !hotspots.length && <tr><td colSpan={6} className="py-8 text-center text-slate-400">No shipment geography yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
